<template>
    <div class="successful_medical_case">
        <div class="smc_header">
            <div class="smc_disease_tag">
                <span class="dot"></span>
                <span v-if="isPad">您的的病种</span>
                <span v-else>患者的病种</span>
            </div>
            <div class="smc_title">{{ diseaseName || '成功病案' }}</div>
            <div class="smc_desc">同病种成功病案展示 · 以下均为慈脉中医治愈的真实案例，病案信息已严格脱敏</div>
        </div>
        <div class="smc_body">
            <div class="smc_list">
                <div v-for="(item, index) in list" :key="index" class="smc_card" @click="viewDetail(item)">
                    <img v-if="item.acknowledgementPhoto" :src="getPath(item.acknowledgementPhoto)" />
                    <div class="title">{{ item.endDate }}</div>
                </div>
            </div>
            <div v-if="list.length === 0 && !loading" class="smc_empty">
                <empty />
            </div>
            <div v-if="hasMore && list.length > 0" class="smc_load_more" @click="loadMore">
                <Icon type="ios-arrow-down" />
                <span>加载更多成功病案</span>
            </div>
            <div v-if="!hasMore && list.length > 0" class="smc_tip"><i></i><span>已展示此病种全部成功病案</span><i></i></div>

            <!-- 视频展示 -->
            <div class="smc_video_section" v-if="isPad">
                <div class="smc_section_title">
                    <span class="dot"></span>
                    成功病案视频
                </div>
                <div class="smc_video_list">
                    <div v-for="(item, index) in videoList" :key="index" class="smc_video_card"
                        @click="playVideo(item)">
                        <div class="smc_video_cover">
                            <video :src="cleanVideoUrl(item.string3)" muted preload="metadata" playsinline
                                class="smc_video_preview"></video>
                            <div class="smc_video_play_overlay">
                                <Icon type="md-arrow-dropright-circle" />
                            </div>
                            <div class="smc_video_duration">{{ item.string5 }}</div>
                        </div>
                        <div class="smc_video_title">{{ item.string4 }}</div>
                    </div>
                </div>
                <empty v-if="videoList.length === 0" />
            </div>
        </div>

        <!-- 视频播放弹窗 -->
        <Modal v-model="videoVisible" :width="'80%'" footer-hide :mask-closable="true" class="smc_video_modal"
            :title="currentVideo?.string4 || '视频播放'">
            <div class="smc_video_player_wrap">
                <video v-if="videoVisible" ref="videoPlayer" :src="cleanVideoUrl(currentVideo?.string3)" controls
                    autoplay class="smc_video_player" playsinline></video>
            </div>
        </Modal>

        <Modal v-model="detailVisible" :width="isPad ? '90%' : '900px'" footer-hide :mask-closable="false">
            <div v-if="currentDetail" class="smc_detail">
                <!-- 左列：图片轮播 + 治愈情况 -->
                <div class="smc_detail_left" v-if="detailPictures.length">
                    <div class="smc_images_carousel">
                        <img :src="detailPictures[currentImgIndex]" class="smc_detail_img"
                            @click="previewImage(detailPictures[currentImgIndex])" 
                            />
                        <div v-if="detailPictures.length > 1" class="carousel-btn carousel-prev" @click="prevImg">
                            <Icon type="ios-arrow-back" />
                        </div>
                        <div v-if="detailPictures.length > 1" class="carousel-btn carousel-next" @click="nextImg">
                            <Icon type="ios-arrow-forward" />
                        </div>
                        <div v-if="detailPictures.length > 1" class="carousel-dots">
                            <span v-for="(_, i) in detailPictures" :key="i" :class="{ active: i === currentImgIndex }"
                                @click="currentImgIndex = i"></span>
                        </div>
                    </div>
                </div>

                <!-- 右列：患者信息 + 基本信息 + 统计 + 治愈情况 -->
                <div class="smc_detail_right">
                    <div class="smc_detail_patient">
                        <div class="smc_info">
                            <span class="smc_name">{{ currentDetail.patientName }}</span>
                            <span class="smc_sub">{{ currentDetail.genderName }} · {{ currentDetail.patientAge
                            }}岁</span>
                        </div>
                    </div>

                    <div class="smc_detail_section">
                        <div class="smc_info_list">
                            <div class="smc_info_row">
                                <div class="smc_info_label">病种</div>
                                <div class="smc_info_value">{{ currentDetail.diseaseName }}</div>
                            </div>
                            <div class="smc_info_row">
                                <div class="smc_info_label">首次看诊</div>
                                <div class="smc_info_value">{{ currentDetail.startDate }}</div>
                            </div>
                            <div class="smc_info_row">
                                <div class="smc_info_label">末次看诊</div>
                                <div class="smc_info_value">{{ currentDetail.endDate }}</div>
                            </div>

                            <div class="smc_info_row">
                                <div class="smc_info_label">看诊次数</div>
                                <div class="smc_info_value">{{ currentDetail.visitTimes || 0 }}</div>
                            </div>
                            <div class="smc_info_row">
                                <div class="smc_info_label">服药贴数</div>
                                <div class="smc_info_value">{{ currentDetail.cureDay || 0 }}</div>
                            </div>
                            <div class="smc_info_row">
                                <div class="smc_info_label">诊疗周期（天）</div>
                                <div class="smc_info_value">{{ currentDetail.cureCycle || 0 }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="smc_detail_section" v-if="currentDetail.remarks">
                        <div class="smc_detail_remark">
                            <div class="smc_section_title">治愈情况</div>
                            <div class="smc_remark_text">{{ currentDetail.remarks }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
    props: {
        info: {
            type: Object,
            default: () => {
                return {};
            }
        },
        isPad: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            list: [],
            pageSize: 6,
            pageIndex: 1,
            total: 0,
            hasMore: true,
            diseaseName: '',
            detailVisible: false,
            currentDetail: null,
            currentImgIndex: 0,
            videoVisible: false,
            currentVideo: null
        };
    },
    computed: {
        ...mapGetters([
            "dicts",
        ]),
        Patient_Health_Video() {
            return this.dicts["Patient_Health_Video"] || [];
        },
        videoList() {
            const diseaseId = this.info.diseaseId;
            return this.Patient_Health_Video.filter(v => v.string1 === String(diseaseId));
        },
        detailPictures() {
            if (!this.currentDetail || !this.currentDetail.pictureUrl) return [];
            let type = this.currentDetail.bscType == 1 ? "/backups/" : "/smedical/";
            return this.currentDetail.pictureUrl.split(',').filter(u => u).map(u => this.cloudPic + type + u);
        }
    },
    methods: {
        cleanVideoUrl(url) {
            if (!url) return '';
            return url.trim().replace(/^`+|`+$/g, '').trim();
        },
        playVideo(item) {
            this.currentVideo = item;
            this.videoVisible = true;
        },
        getPath(urlStr) {
            if (!urlStr) return '';
            return this.cloudPic + "/other/" + urlStr;
        },
        getFirstPicture(urlStr) {
            if (!urlStr) return '';
            const urls = urlStr.split(',');
            return urls[0] || '';
        },
        getPictures(urlStr) {
            if (!urlStr) return [];
            let type = this.currentDetail.bscType == 1 ? "/backups/" : "/smedical/";
            return urlStr.split(',').filter(u => u).map(u => this.cloudPic + type + u);
        },
        previewImage(url) {
            window.open(url, '_blank');
        },
        viewDetail(item) {
            this.currentDetail = item;
            this.currentImgIndex = 0;
            this.detailVisible = true;
        },
        prevImg() {
            const len = this.detailPictures.length;
            if (!len) return;
            this.currentImgIndex = (this.currentImgIndex - 1 + len) % len;
        },
        nextImg() {
            const len = this.detailPictures.length;
            if (!len) return;
            this.currentImgIndex = (this.currentImgIndex + 1) % len;
        },
        loadMore() {
            if (this.loading) return;
            this.pageIndex++;
            this.getData();
        },
        getData() {
            if (this.loading) return;
            this.loading = true;
            this.api2('showEnterprisePatientHealthRecord', {
                diseaseId: this.info.diseaseId,
                pageSize: this.pageSize,
                pageIndex: this.pageIndex,
                sysId: 0, //系统Id 0云诊  1其他
            }).then(res => {
                if (res) {
                    this.total = res.total || 0;
                    const newList = res.result || [];
                    if (this.pageIndex === 1) {
                        this.list = newList;
                    } else {
                        this.list = this.list.concat(newList);
                    }
                    this.hasMore = this.list.length < this.total;
                    if (newList.length > 0 && !this.diseaseName) {
                        this.diseaseName = newList[0].diseaseName;
                    }
                }
                this.loading = false;
            }).catch(e => {
                console.error('获取成功病案失败:', e);
                this.loading = false;
            });
        },
        init() {
            this.pageIndex = 1;
            this.list = [];
            this.diseaseName = '';
            this.getData();
        }
    },
    created() {
        this.init();
        this.$store.dispatch("getWideUseList", {
            key: "Patient_Health_Video",
            type: "saas",
        });
    },
    watch: {
        info: {
            handler(newVal, oldVal) {
                if (newVal.patientCode !== oldVal.patientCode) {
                    this.init();
                }
            },
            deep: true
        },
    }
};
</script>

<style lang="scss" scoped>
.successful_medical_case {
    height: 100%;
    overflow-y: auto;
}

.smc_header {
    text-align: center;
    margin-bottom: 15px;
}

.smc_disease_tag {
    display: inline-flex;
    align-items: center;
    padding: 6px 16px;
    background: #f0e7e5;
    border-radius: 20px;
    color: #692d23;
    font-size: 13px;
    margin-bottom: 5px;
}

.smc_disease_tag .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #D4A574;
    margin-right: 8px;
}

.smc_title {
    font-size: 28px;
    font-weight: bold;
    color: #6B4423;
    margin-bottom: 5px;
    letter-spacing: 2px;
}

.smc_desc {
    font-size: 13px;
    color: #A08B70;
    line-height: 1.6;
}

.smc_body {
    position: relative;
}

.smc_list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;

    img {
        width: 100%;
        height: 240px;
        vertical-align: top;
    }

    .title {
        padding: 8px 15px;
    }
}

.smc_card {
    overflow: hidden;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid #e2d6d5;
    box-shadow: 0 2px 8px rgba(139, 94, 60, 0.06);
}
.smc_card>img {
    display: block;
    width: 100%;
    height: 240px;
    object-fit: cover;
    object-position: center;
}

.smc_card:hover {
    box-shadow: 0 6px 20px rgba(139, 94, 60, 0.12);
    transform: translateY(-3px);
}

.smc_card_header {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    background: #f6eeec;
}

.smc_avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #692d23 0%, #A67B5B 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    color: #fff;
    font-size: 17px;
    font-weight: bold;
    box-shadow: 0 2px 6px rgba(139, 94, 60, 0.25);
}

.smc_avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.smc_info {
    flex: 1;

    .smc_name {
        padding-right: 15px;
        font-size: 15px;
        font-weight: bold;
        color: #5D4037;
        margin-bottom: 0px;
    }
}

.smc_sub {
    font-size: 12px;
    color: #A08B70;
}

.smc_tag {
    display: flex;
    align-items: center;
    padding: 4px 10px;
    background: #fffce0;
    border-radius: 12px;
    color: #8a7015;
    font-size: 11px;
    font-weight: 500;
}

.smc_tag .ivu-icon,
.smc_tag .iconfont {
    margin-right: 3px;
    font-size: 12px;
}

.smc_card_content {
    padding: 15px 15px 10px;
}

.smc_card_stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 16px;
}

.smc_stat {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    background: #f6eeec;
    border-radius: 8px;
    border: 1px solid #f6eeec;
}

.smc_stat .ivu-icon,
.smc_stat .iconfont {
    font-size: 16px;
    color: #A67B5B;
    margin-right: 8px;
    flex-shrink: 0;
    padding: 5px;
    background: #fff;
    border-radius: 50%;
}

.smc_stat_info {
    flex: 1;
    min-width: 0;
}

.smc_stat_label {
    font-size: 11px;
    color: #A08B70;
    margin-bottom: 2px;
}

.smc_stat_value {
    font-size: 13px;
    color: #5D4037;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.smc_card_remark {
    background: #fafafa;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    border-left: 3px solid #692d23;
    height: 108px;
}

.smc_remark_title {
    font-size: 12px;
    color: #6B4423;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

.smc_remark_title .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #692d23;
    margin-right: 8px;
}

.smc_remark_content {
    font-size: 12px;
    color: #8B7355;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.smc_card_footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 8px;
    border-top: 1px dashed #E8D5B7;
    color: #692d23;
    font-size: 13px;
    font-weight: 500;
}

.smc_card_footer .ivu-icon,
.smc_card_footer .iconfont {
    margin-left: 4px;
    font-size: 14px;
}

.smc_view_detail {
    cursor: pointer;
}

.smc_view_detail:hover {
    text-decoration: underline;
}

.smc_empty {
    padding: 60px 0;
    text-align: center;
}

.smc_load_more {
    text-align: center;
    padding: 14px 30px;
    margin: 20px auto 0;
    background: #F5E6D3;
    border-radius: 24px;
    color: #6B4423;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    display: block;
    width: fit-content;

    .ivu-icon {
        font-size: 18px;
        margin-right: 5px;
    }
}

.smc_load_more:hover {
    background: #E8D4B8;
    transform: scale(1.02);
}

.smc_detail {
    padding: 10px 0;
    display: flex;
    gap: 24px;
}

.smc_detail_left {
    flex: 0 0 45%;
    min-width: 0;

    .smc_section_title {
        margin-bottom: 12px;
    }
}

.smc_detail_right {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    max-height: 70vh;
}

/* —— 图片轮播 —— */
.smc_images_carousel {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
    overflow: hidden;
    background: #f5f0ea;
}

.smc_images_carousel .smc_detail_img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: zoom-in;
    display: block;
}

.smc_images_carousel .carousel-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    color: #692d23;
    font-size: 16px;
    z-index: 2;
    transition: all 0.2s;

    &:hover {
        background: #fff;
        transform: translateY(-50%) scale(1.08);
    }
}

.smc_images_carousel .carousel-prev {
    left: 10px;
}

.smc_images_carousel .carousel-next {
    right: 10px;
}

.smc_images_carousel .carousel-dots {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 2;
}

.smc_images_carousel .carousel-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    transition: all 0.2s;

    &.active {
        background: #fff;
        width: 18px;
        border-radius: 4px;
    }
}

.smc_detail_patient {
    margin-bottom: 10px;
}

.smc_detail_section {
    margin-bottom: 18px;
}

.smc_section_title {
    font-weight: bold;
    color: #6B4423;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
}

.smc_section_title .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #692d23;
    margin-right: 10px;
}

/* —— 基本信息 改为单列行列表单样式，更贴近截图 —— */
.smc_info_list {
    display: flex;
    flex-direction: column;
}

.smc_info_row {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px dashed #00000011; 

    &:last-child {
        border-bottom: none;
    }
}

.smc_info_row .smc_info_label {
    flex: 0 0 120px;
    font-size: 14px;
    color: #A08B70;
}

.smc_info_row .smc_info_value {
    flex: 1;
    font-size: 14px;
    color: #5D4037;
    font-weight: 500;
}

.smc_detail_stats {
    display: flex;
    justify-content: space-around;
    padding: 14px 0;
    background: #FAF6F0;
    border-radius: 10px;
    margin-bottom: 18px;
    border: 1px solid #F0E5D5;
}

.smc_detail_stat {
    text-align: center;
    flex: 1;
    position: relative;

    &+.smc_detail_stat {
        &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            transform: translate(0, -50%);
            width: 1px;
            height: 30px;
            background: #F0E5D5;
        }
    }
}

.smc_detail_stat_num {
    font-size: 26px;
    height: 32px;
    line-height: 32px;
    font-weight: bold;
    color: #692d23;
}

.smc_detail_stat_label {
    font-size: 13px;
    color: #A08B70;
}

.smc_detail_remark {
    padding: 14px 16px;
    background: #FDF8F0;
    border-radius: 10px;
    border-left: 3px solid #692d23;
}

.smc_remark_text {
    font-size: 14px;
    color: #6B5540;
    line-height: 1.8;
}

/* 兼容旧样式里可能还被引用的类 */
.smc_info_grid,
.smc_info_item,
.smc_detail_images {
    display: none;
}

@media (max-width: 900px) {
    .smc_detail {
        flex-direction: column;
    }

    .smc_detail_left {
        flex: none;
    }

    .smc_detail_right {
        max-height: none;
    }
}

.smc_tip {
    font-size: 12px;
    color: #A08B70;
    margin: 30px 0;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        margin: 0 12px;
    }

    i {
        border-bottom: 1px solid #ccc;
        width: 100px;
        height: 1px;
    }
}

.smc_video_section {
    margin-top: 30px;
}

.smc_video_list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
}

.smc_video_card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e2d6d5;
    box-shadow: 0 2px 8px rgba(139, 94, 60, 0.06);
    background: #fff;
}

.smc_video_card:hover {
    box-shadow: 0 6px 20px rgba(139, 94, 60, 0.15);
    transform: translateY(-3px);
}

.smc_video_cover {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    background: #000;
    overflow: hidden;
}

.smc_video_preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
}

.smc_video_play_overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.25);
    transition: background 0.3s;

    .ivu-icon,
    .iconfont {
        font-size: 52px;
        color: #692d23;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
        transition: transform 0.3s;
    }
}

.smc_video_card:hover .smc_video_play_overlay {
    background: rgba(0, 0, 0, 0.15);

    .ivu-icon,
    .iconfont {
        transform: scale(1.15);
    }
}

.smc_video_duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
}

.smc_video_title {
    padding: 10px 12px;
    font-size: 14px;
    color: #5D4037;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 1200px) {
    .smc_list {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .smc_list {
        grid-template-columns: 1fr;
    }
}
</style>

<style lang="scss">
/* Modal 内容被 teleport 到 document 层级，需非 scoped 样式 */
.smc_video_modal .smc_video_player_wrap {
    background: #000;
    border-radius: 8px;
    overflow: hidden;
}

.smc_video_modal .smc_video_player {
    width: 100%;
    max-height: 75vh;
    display: block;
}
</style>
